import { BaseAssetType } from 'core/logic/asset/asset.types'
import { DeliveryType } from 'core/logic/delivery/delivery.types'
import { GalleryBound } from 'core/logic/gallery/gallery.types'
import { MY_COLLECTION_SUB_MENUS } from 'openspace/pages/Authenticated/MyCollection/MyCollection.view'

export const SIGN_IN = 'sign-in'
export const SIGN_UP = 'sign-up'
export const DROPS = 'drops'
export const CHECKOUT = 'checkout'
export const CONFIRM = 'confirm'
export const PAYMENT = 'payment'
export const PENDING = 'pending'
export const OPEN = 'open'
export const OPENING = 'opening'
export const WAITING_ROOM = 'waiting'
export const MY_COLLECTION = 'collection'
export const TERMS = 'terms'
export const PACKS = 'packs'
export const DETAIL = 'detail'
export const ACCOUNT = 'account'
export const ASSET = 'asset'
export const GALLERY = 'g'
export const FAQ = 'faq'

export const TO_MAINPAGE = () => '/'
export const TO_METAMASK = { [SIGN_IN]: `/${SIGN_IN}/metamask` }
export const TO_FIREBASE = {
  [SIGN_IN]: `/${SIGN_IN}/firebase`,
  [SIGN_UP]: `/${SIGN_UP}/firebase`,
}
export const TO_ACCOUNT = `/${ACCOUNT}`
export const TO_ASSET_LIST = `/${ASSET}`
export const TO_DROP_DETAILS = (dropId: string) => `/${DROPS}/${dropId}`
export const TO_DROP_CHECKOUT = (dropId: string) =>
  `/${DROPS}/${dropId}/${CHECKOUT}`
export const TO_DROP_PAYMENT = (dropId: string, quantity?: number) =>
  `/${DROPS}/${dropId}/${PAYMENT}?amount=${quantity}`
export const TO_MARKETPLACE_PAYMENT = (
  offerId: string,
  marketplaceOfferId: string
) =>
  ROUTES.authenticated.marketplacePayment
    .replace(':marketplaceOfferId', marketplaceOfferId)
    .replace(':dropId', offerId)
export const TO_DROP_PENDING_PAYMENT = (purchaseId: string) =>
  `/${PAYMENT}/${purchaseId}`
export const TO_WAITING_ROOM = (dropId: string) => `/${WAITING_ROOM}/${dropId}`
export const TO_DROP_OPEN = (deliveryId: string) =>
  `/${PACKS}/${deliveryId}/${OPEN}`
export const TO_DROP_OPENING = (deliveryId: string) =>
  `/${PACKS}/${deliveryId}/${OPENING}`
export const TO_MY_COLLECTION = (subMenu?: MY_COLLECTION_SUB_MENUS) =>
  `/${MY_COLLECTION}`.concat(subMenu ? `?at=${subMenu}` : '')
export const TO_COLLECTION_ITEM_DETAIL = (itemId: string, tokenId: number) =>
  `/${MY_COLLECTION}/${itemId}?tokenId=${tokenId}`
export const TO_ASSET_DETAIL = (assetId: string) => `/${ASSET}/${assetId}`
export const TO_GALLERY_DETAIL = (galleryId: string) =>
  `/${GALLERY}/${galleryId}`
export const TO_FAQ = `/${FAQ}`
export const TO_SIGNIN = () => {
  return `/${SIGN_IN}/firebase`
}
export const TO_MARKETPLACE = () => ROUTES_UNAUTHENTICATED.marketplace
export const TO_MARKETPLACE_SELL = (
  assetId: string,
  tokenId: number | undefined
) =>
  ROUTES_AUTHENTICATED.sellMarketplace
    .replace(':assetId', assetId)
    .concat(tokenId !== undefined ? `?tokenId=${tokenId}` : '')
export const TO_MARKETPLACE_OFFER_EDIT = (assetId: string, offerId: string) =>
  ROUTES_AUTHENTICATED.editOffer
    .replace(':offerId', offerId)
    .replace(':assetId', assetId)
export const TO_MARKETPLACE_OFFER_DETAILS = (
  assetId: string,
  offerId: string,
  tokenId: number
) =>
  ROUTES_AUTHENTICATED.offerItemDetail
    .replace(':offerId', offerId)
    .replace(':assetId', assetId)
    .concat(tokenId !== undefined ? `?tokenId=${tokenId}` : '')

export const TO_MARKETPLACE_OFFER_BUY = (assetId: string, offerId: string) => {
  return ROUTES.authenticated.marketplaceOfferBuy
    .replace(':assetId', assetId)
    .replace(':offerId', offerId)
}

export const TO_SHIPPING_TRACK = (trackingId: string) => {
  return ROUTES.authenticated.tracking.replace(':trackingId', trackingId)
}

/** If there is a gallery id associated with the entity, add the gallery id preffix to the route */
export const GALLERY_BOUND = <
  X extends GalleryBound,
  T extends (entity: X, ...args: any[]) => string
>(
  pathBuilder: T
): T => {
  return ((entity: X, ...args: any[]) => {
    if (entity.galleryId !== undefined)
      return TO_GALLERY(entity.galleryId)(pathBuilder(entity, ...args))
    else return pathBuilder(entity, ...args)
  }) as T
}
export const TO_MARKETPLACE_ASSET_DETAILS = GALLERY_BOUND(
  (asset: Pick<BaseAssetType, 'id'>, offerId: string | undefined) =>
    `${ROUTES_UNAUTHENTICATED.marketplaceAssetDetail}?offerId=:offerId`
      .replace(':assetId', asset.id)
      .replace(':offerId', offerId || '')
)
export const TO_CREATORS_SECTION = (section: 'brand' | 'creators') =>
  ROUTES_UNAUTHENTICATED.creators.replace(':section', section)

export const TO_FEATURE_DETAILS = (feature: string) => {
  return ROUTES_UNAUTHENTICATED.featureDetails.replace(':featureId', feature)
}

export const TO_GALLERY = (galleryId: string) => (route: string) =>
  `/g/${galleryId}${route}`

export const TO_CHALLENGE = (challengeId: string) =>
  ROUTES.unauthenticated.challengeDetails.replace(':challengeId', challengeId)

export const TO_AIRDROP = (airdropId: string) => {
  return `${ROUTES.unauthenticated.claim}?airdropId=${airdropId}`
}

export const TO_TRACKING_PAGE = (deliveryId: DeliveryType['id']) => {
  return `${ROUTES.authenticated.tracking.replace(':deliveryId', deliveryId)}`
}

const ROUTES_UNAUTHENTICATED = Object.freeze({
  homepage: '/',
  terms: `/${TERMS}`,
  risk: `/risk`,
  regulation: `/regulation`,
  signIn: `/${SIGN_IN}/:provider`,
  signUp: `/${SIGN_UP}/:provider`,
  dropDetails: `/${DROPS}/:dropId`,
  asset: `/${ASSET}`,
  assetDetail: `/${ASSET}/:assetId`,
  marketplaceAssetDetail: `/marketplace/${ASSET}/:assetId`,
  galleryDetail: `/${GALLERY}/:galleryId`,
  allGalleries: `/${GALLERY}`,
  faq: `/${FAQ}`,
  marketplace: `/marketplace`,
  galleryBoundMarketplace: `/g/:galleryId/marketplace`,
  claim: `/airdrop`,
  claimSignup: `/claim/signup`,
  featureDetails: `/feature/:featureId`,
  creators: `/creators/:section`,
  challengeDetails: `/challenges/:challengeId`,
})

const ROUTES_AUTHENTICATED = Object.freeze({
  checkout: `/${DROPS}/:dropId/${CHECKOUT}`,
  payment: `/${DROPS}/:dropId/${PAYMENT}`,
  pendingPayment: `/${PAYMENT}/:purchaseId`,
  confirmPayment: `/${PAYMENT}/:purchaseId/${CONFIRM}`,
  open: `/${PACKS}/:deliveryId/${OPEN}`,
  opening: `/${PACKS}/:deliveryId/${OPENING}`,
  waitingRoom: `/${WAITING_ROOM}/:dropId`,
  myCollection: `/${MY_COLLECTION}`,
  collectionItemDetail: `/${MY_COLLECTION}/:assetId`,
  userAccount: `/${ACCOUNT}`,
  marketplaceOfferBuy: `/marketplace/${ASSET}/:assetId/${PAYMENT}/:offerId`,
  sellMarketplace: `/marketplace/${ASSET}/:assetId/sell`,
  editOffer: `/marketplace/:assetId/offer/:offerId/edit`,
  offerItemDetail: `/marketplace/:assetId/offer/:offerId`,
  transferTokens: '/transfer/confirm',
  marketplacePayment: `/drops/:dropId/marketplace/:marketplaceOfferId/payment`,
  tracking: `/${PACKS}/:deliveryId/tracking`,
})

const ROUTES = {
  unauthenticated: ROUTES_UNAUTHENTICATED,
  authenticated: ROUTES_AUTHENTICATED,
}

export const GALLERY_BOUND_ROUTES = {
  authenticated: Object.entries(ROUTES_AUTHENTICATED).reduce(
    (r, [k, v]) => ({
      ...r,
      [k]: TO_GALLERY(':galleryId')(v),
    }),
    {} as typeof ROUTES_AUTHENTICATED
  ),
  unauthenticated: Object.entries(ROUTES_UNAUTHENTICATED).reduce(
    (r, [k, v]) => ({
      ...r,
      [k]: TO_GALLERY(':galleryId')(v),
    }),
    {} as typeof ROUTES_UNAUTHENTICATED
  ),
}

export default ROUTES
